import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  addDays, addMonths, format, isSameMonth, isWithinInterval,
  parseISO, startOfMonth, startOfWeek,
} from "date-fns";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { can } from "../lib/permissions";
import { readableTextOn } from "../lib/colors";
import type { Employee, OnCallAssignment, OnCallRoster } from "../lib/types";
import { Button } from "../components/ui/Button";
import { Field, Input, Select } from "../components/ui/Input";
import { Modal } from "../components/ui/Modal";
import { RemoveButton } from "../components/ui/RemoveButton";

// On-Call page — dedicated calendar surface for specialty rotations
// that live OUTSIDE the shift schedule (Detectives, SWAT, K-9, …).
// This page never reads or writes shift_assignments / assignments;
// the on_call_* tables are an island.
export function OnCall() {
  return <OnCallInner />;
}

function OnCallInner() {
  const me = useAuth((s) => s.user);
  const canEdit = can.editRoster(me); // L2+ for assignments
  const qc = useQueryClient();
  const [activeRosterId, setActiveRosterId] = useState<string | "manage" | null>(null);
  const [viewMonth, setViewMonth] = useState(() => startOfMonth(new Date()));

  const rostersQ = useQuery({
    queryKey: ["onCallRosters"],
    queryFn: () => api.listOnCallRosters(false),
  });
  const employeesQ = useQuery({
    queryKey: ["employees"],
    queryFn: () => api.listEmployees(),
  });

  const rosters = useMemo(() => rostersQ.data ?? [], [rostersQ.data]);
  const activeRoster: OnCallRoster | null = useMemo(() => {
    if (!activeRosterId || activeRosterId === "manage") return rosters[0] ?? null;
    return rosters.find((r) => r.id === activeRosterId) ?? rosters[0] ?? null;
  }, [activeRosterId, rosters]);

  // Calendar viewport: full weeks covering the visible month.
  const monthGridDays = useMemo(() => {
    const first = startOfMonth(viewMonth);
    const gridStart = startOfWeek(first, { weekStartsOn: 0 });
    // Always 6 rows × 7 = 42 days so the grid height doesn't jitter
    // month to month.
    return Array.from({ length: 42 }, (_, i) => addDays(gridStart, i));
  }, [viewMonth]);

  const gridStartISO = format(monthGridDays[0], "yyyy-MM-dd");
  const gridEndISO = format(monthGridDays[monthGridDays.length - 1], "yyyy-MM-dd");

  const assignmentsQ = useQuery({
    queryKey: ["onCallAssignments", activeRoster?.id, gridStartISO, gridEndISO],
    queryFn: () => api.listOnCallAssignments(activeRoster!.id, gridStartISO, gridEndISO),
    enabled: !!activeRoster,
  });

  const showingManage = activeRosterId === "manage";

  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto space-y-4">
      <header className="flex items-baseline justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-xl font-semibold text-slate-900">On-Call</h1>
          <p className="text-sm text-slate-500">
            Specialty rotations. Independent of the shift schedule — no
            coverage or OT impact.
          </p>
        </div>
      </header>

      {/* Roster tabs. "+ Manage" sits at the right; clicking it
        * swaps the body to the roster-management surface. */}
      <div className="flex flex-wrap items-center gap-2 border-b border-slate-200">
        {rosters.length === 0 && !showingManage && (
          <div className="text-sm text-slate-500 py-3">
            No rosters yet. <button
              type="button"
              onClick={() => setActiveRosterId("manage")}
              className="text-sky-600 hover:underline">Create your first roster</button>.
          </div>
        )}
        {rosters.map((r) => {
          const isActive = !showingManage && activeRoster?.id === r.id;
          return (
            <button
              key={r.id}
              type="button"
              onClick={() => setActiveRosterId(r.id)}
              className={
                "px-3 py-2 -mb-px text-sm font-medium border-b-2 transition " +
                (isActive
                  ? "border-sky-500 text-slate-900"
                  : "border-transparent text-slate-600 hover:text-slate-900")
              }
            >
              <span
                className="inline-block w-2.5 h-2.5 rounded-full mr-1.5 align-middle"
                style={{ backgroundColor: r.color }}
              />
              {r.name}
            </button>
          );
        })}
        {canEdit && (
          <button
            type="button"
            onClick={() => setActiveRosterId("manage")}
            className={
              "ml-auto px-3 py-2 -mb-px text-sm font-medium border-b-2 transition " +
              (showingManage
                ? "border-sky-500 text-slate-900"
                : "border-transparent text-slate-500 hover:text-slate-900")
            }
          >
            ⚙ Manage rosters
          </button>
        )}
      </div>

      {showingManage ? (
        <ManageRosters
          rosters={rosters}
          onSelect={(id) => setActiveRosterId(id)}
          onChanged={() => qc.invalidateQueries({ queryKey: ["onCallRosters"] })}
        />
      ) : activeRoster ? (
        <MonthView
          roster={activeRoster}
          viewMonth={viewMonth}
          setViewMonth={setViewMonth}
          monthGridDays={monthGridDays}
          assignments={assignmentsQ.data ?? []}
          employees={employeesQ.data ?? []}
          canEdit={canEdit}
          onChanged={() => qc.invalidateQueries({ queryKey: ["onCallAssignments"] })}
        />
      ) : null}
    </div>
  );
}

// -------------- Month view --------------

interface MonthViewProps {
  roster: OnCallRoster;
  viewMonth: Date;
  setViewMonth: (d: Date) => void;
  monthGridDays: Date[];
  assignments: OnCallAssignment[];
  employees: Employee[];
  canEdit: boolean;
  onChanged: () => void;
}

function MonthView(p: MonthViewProps) {
  const [editing, setEditing] = useState<{
    date: Date | null;
    existing: OnCallAssignment | null;
  } | null>(null);

  const empById = useMemo(() => {
    const m = new Map<string, Employee>();
    for (const e of p.employees) m.set(e.id, e);
    return m;
  }, [p.employees]);

  // Index assignments by date for O(1) lookup per cell.
  const assignmentsByDate = useMemo(() => {
    const m = new Map<string, OnCallAssignment[]>();
    for (const day of p.monthGridDays) {
      const k = format(day, "yyyy-MM-dd");
      const matches = p.assignments.filter((a) =>
        isWithinInterval(day, {
          start: parseISO(a.startDate),
          end: parseISO(a.endDate),
        }),
      );
      // Primary first, then secondary — predictable display order.
      matches.sort((x, y) => x.role.localeCompare(y.role));
      m.set(k, matches);
    }
    return m;
  }, [p.monthGridDays, p.assignments]);

  return (
    <div className="space-y-3">
      {/* Month nav + roster meta strip */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Button
            variant="secondary"
            onClick={() => p.setViewMonth(addMonths(p.viewMonth, -1))}
          >
            ←
          </Button>
          <div className="text-base font-semibold text-slate-900 min-w-[10ch] text-center">
            {format(p.viewMonth, "MMMM yyyy")}
          </div>
          <Button
            variant="secondary"
            onClick={() => p.setViewMonth(addMonths(p.viewMonth, 1))}
          >
            →
          </Button>
          <Button
            variant="secondary"
            onClick={() => p.setViewMonth(startOfMonth(new Date()))}
          >
            Today
          </Button>
        </div>
        <div className="text-xs text-slate-500 flex items-center gap-3 flex-wrap">
          {p.roster.radioChannel && (
            <span>📻 <span className="font-medium text-slate-700">{p.roster.radioChannel}</span></span>
          )}
          {p.roster.contactPhone && (
            <span>☎ <span className="font-medium text-slate-700">{p.roster.contactPhone}</span></span>
          )}
          {p.canEdit && (
            <Button onClick={() => setEditing({ date: null, existing: null })}>
              + Add assignment
            </Button>
          )}
        </div>
      </div>

      {/* Calendar grid */}
      <div className="grid grid-cols-7 border border-slate-200 rounded-lg overflow-hidden bg-white">
        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
          <div
            key={d}
            className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold bg-slate-50 border-b border-slate-200 px-2 py-1.5 text-center"
          >
            {d}
          </div>
        ))}
        {p.monthGridDays.map((day, i) => {
          const inMonth = isSameMonth(day, p.viewMonth);
          const k = format(day, "yyyy-MM-dd");
          const rows = assignmentsByDate.get(k) ?? [];
          const isToday = format(new Date(), "yyyy-MM-dd") === k;
          return (
            <button
              type="button"
              key={k + i}
              disabled={!p.canEdit}
              onClick={() =>
                p.canEdit && setEditing({ date: day, existing: null })
              }
              className={
                "relative min-h-[5.5rem] text-left border-t border-l border-slate-100 transition overflow-hidden " +
                ((i + 1) % 7 === 0 ? "border-r " : "") +
                (i >= 35 ? "border-b " : "") +
                (inMonth ? "bg-white " : "bg-slate-50/60 ") +
                (p.canEdit ? "hover:bg-sky-50/60 cursor-pointer " : "cursor-default ")
              }
            >
              {/* Chip(s) fill the entire cell. When there's both a
                * primary and a secondary, they split the vertical
                * space evenly. */}
              {rows.length > 0 && (
                <div className="absolute inset-0 flex flex-col">
                  {rows.map((a) => {
                    const emp = empById.get(a.employeeId);
                    const label = emp
                      ? `${emp.lastName}${emp.firstName ? ", " + emp.firstName[0] + "." : ""}`
                      : "Unknown";
                    const isSecondary = a.role === "secondary";
                    return (
                      <div
                        key={a.id}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (p.canEdit) setEditing({ date: day, existing: a });
                        }}
                        className="flex-1 flex items-center justify-center text-xs font-semibold leading-tight px-1 truncate"
                        style={{
                          backgroundColor: p.roster.color,
                          color: readableTextOn(p.roster.color),
                          opacity: isSecondary ? 0.72 : 1,
                        }}
                        title={
                          `${label} · ${a.role}` +
                          (a.notes ? ` · ${a.notes}` : "") +
                          ` · ${a.startDate}…${a.endDate}`
                        }
                      >
                        {isSecondary && (
                          <span className="text-[9px] uppercase mr-0.5 opacity-80">2°</span>
                        )}
                        {label}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Day number — anchored top-left, layered on top of
                * any chip. A small white pill keeps the digit
                * readable on any roster color. */}
              <div
                className={
                  "absolute top-1 left-1 z-10 text-[11px] font-semibold leading-none px-1.5 py-0.5 rounded " +
                  (isToday
                    ? "bg-sky-500 text-white"
                    : rows.length > 0
                      ? "bg-white/90 text-slate-700"
                      : (inMonth ? "text-slate-700" : "text-slate-400"))
                }
              >
                {format(day, "d")}
              </div>
            </button>
          );
        })}
      </div>

      {editing && (
        <AssignmentEditor
          roster={p.roster}
          employees={p.employees}
          initialDate={editing.date}
          existing={editing.existing}
          onClose={() => setEditing(null)}
          onChanged={() => {
            p.onChanged();
            setEditing(null);
          }}
        />
      )}
    </div>
  );
}

// -------------- Assignment editor --------------

interface AssignmentEditorProps {
  roster: OnCallRoster;
  employees: Employee[];
  initialDate: Date | null;
  existing: OnCallAssignment | null;
  onClose: () => void;
  onChanged: () => void;
}

function AssignmentEditor(p: AssignmentEditorProps) {
  const [deleteArmed, setDeleteArmed] = useState(false);
  const [employeeId, setEmployeeId] = useState(p.existing?.employeeId ?? "");
  const [startDate, setStartDate] = useState(
    p.existing?.startDate ?? (p.initialDate ? format(p.initialDate, "yyyy-MM-dd") : format(new Date(), "yyyy-MM-dd")),
  );
  const [endDate, setEndDate] = useState(
    p.existing?.endDate ?? (p.initialDate ? format(p.initialDate, "yyyy-MM-dd") : format(new Date(), "yyyy-MM-dd")),
  );
  const [role, setRole] = useState<"primary" | "secondary">(
    p.existing?.role ?? "primary",
  );
  const [notes, setNotes] = useState(p.existing?.notes ?? "");
  const [error, setError] = useState<string | null>(null);

  const activeEmployees = useMemo(
    () => p.employees
      .filter((e) => e.status === "active")
      .sort((a, b) => a.lastName.localeCompare(b.lastName)),
    [p.employees],
  );

  const saveMut = useMutation({
    mutationFn: async () => {
      if (!employeeId) throw new Error("Pick an officer.");
      if (endDate < startDate) throw new Error("End date can't be before start.");
      if (p.existing) {
        await api.updateOnCallAssignment(p.existing.id, {
          employeeId,
          startDate,
          endDate,
          role,
          notes: notes.trim() || null,
        });
      } else {
        await api.createOnCallAssignment({
          rosterId: p.roster.id,
          employeeId,
          startDate,
          endDate,
          role,
          notes: notes.trim() || null,
        });
      }
    },
    onSuccess: () => p.onChanged(),
    onError: (e: { message?: string }) => setError(e?.message ?? "Failed."),
  });

  const delMut = useMutation({
    mutationFn: () => api.deleteOnCallAssignment(p.existing!.id),
    onSuccess: () => p.onChanged(),
    onError: (e: { message?: string }) => setError(e?.message ?? "Failed."),
  });

  return (
    <Modal
      open
      onClose={p.onClose}
      title={p.existing ? "Edit on-call assignment" : "New on-call assignment"}
      footer={
        <div className="flex items-center justify-between gap-2 w-full">
          <div>
            {p.existing && (
              <RemoveButton
                armed={deleteArmed}
                onArm={() => setDeleteArmed(true)}
                onConfirm={() => delMut.mutate()}
                label="Delete"
                confirmLabel="Confirm delete"
                disabled={delMut.isPending}
              />
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="secondary" onClick={p.onClose}>Cancel</Button>
            <Button
              onClick={() => { setError(null); saveMut.mutate(); }}
              disabled={saveMut.isPending}
            >
              {saveMut.isPending ? "Saving…" : "Save"}
            </Button>
          </div>
        </div>
      }
    >
      <div className="space-y-3 text-sm">
        <div className="text-xs text-slate-500">
          Roster: <span className="font-semibold text-slate-700">{p.roster.name}</span>
        </div>
        <Field label="Officer">
          <Select value={employeeId} onChange={(e) => setEmployeeId(e.target.value)}>
            <option value="">— Select —</option>
            {activeEmployees.map((e) => (
              <option key={e.id} value={e.id}>
                {e.lastName}, {e.firstName}
                {e.rank ? ` · ${e.rank}` : ""}
                {e.radioNumber ? ` · #${e.radioNumber}` : (e.badgeNumber ? ` · #${e.badgeNumber}` : "")}
              </option>
            ))}
          </Select>
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Start date">
            <Input type="date" value={startDate}
              onChange={(e) => setStartDate(e.target.value)} />
          </Field>
          <Field label="End date (inclusive)">
            <Input type="date" value={endDate}
              onChange={(e) => setEndDate(e.target.value)} />
          </Field>
        </div>
        {p.roster.hasSecondary && (
          <Field label="Role">
            <Select value={role} onChange={(e) => setRole(e.target.value as "primary" | "secondary")}>
              <option value="primary">Primary</option>
              <option value="secondary">Secondary</option>
            </Select>
          </Field>
        )}
        <Field label="Notes (optional)">
          <Input value={notes} onChange={(e) => setNotes(e.target.value)} />
        </Field>
        {error && <div className="text-sm text-red-600">{error}</div>}
      </div>
    </Modal>
  );
}

// -------------- Manage rosters --------------

interface ManageRostersProps {
  rosters: OnCallRoster[];
  onSelect: (id: string) => void;
  onChanged: () => void;
}

function ManageRosters(p: ManageRostersProps) {
  const [editing, setEditing] = useState<OnCallRoster | "new" | null>(null);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-sm font-semibold text-slate-800">Rosters</h2>
          <p className="text-xs text-slate-500">
            Add one row per specialty rotation. Names appear as tabs above.
          </p>
        </div>
        <Button onClick={() => setEditing("new")}>+ New roster</Button>
      </div>
      <ul className="divide-y divide-slate-100 border border-slate-200 rounded-md bg-white">
        {p.rosters.length === 0 && (
          <li className="px-3 py-4 text-sm text-slate-500">No rosters yet.</li>
        )}
        {p.rosters.map((r) => (
          <li key={r.id} className="flex items-center justify-between gap-3 px-3 py-2.5">
            <div className="flex items-center gap-2 min-w-0">
              <span
                className="inline-block w-3 h-3 rounded-full shrink-0"
                style={{ backgroundColor: r.color }}
              />
              <div className="min-w-0">
                <button
                  type="button"
                  onClick={() => p.onSelect(r.id)}
                  className="text-sm font-medium text-slate-800 hover:underline truncate"
                >
                  {r.name}
                </button>
                <div className="text-[11px] text-slate-500">
                  {r.hasSecondary ? "Primary + Secondary" : "Primary only"}
                  {r.radioChannel ? ` · 📻 ${r.radioChannel}` : ""}
                  {r.contactPhone ? ` · ☎ ${r.contactPhone}` : ""}
                </div>
              </div>
            </div>
            <Button variant="secondary" onClick={() => setEditing(r)}>Edit</Button>
          </li>
        ))}
      </ul>

      {editing && (
        <RosterEditor
          existing={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onChanged={() => { p.onChanged(); setEditing(null); }}
        />
      )}
    </div>
  );
}

interface RosterEditorProps {
  existing: OnCallRoster | null;
  onClose: () => void;
  onChanged: () => void;
}

const COLOR_PRESETS = [
  "#0ea5e9", "#22c55e", "#a855f7", "#f59e0b", "#ef4444",
  "#14b8a6", "#ec4899", "#64748b",
];

function RosterEditor(p: RosterEditorProps) {
  const [archiveArmed, setArchiveArmed] = useState(false);
  const [name, setName] = useState(p.existing?.name ?? "");
  const [color, setColor] = useState(p.existing?.color ?? "#0ea5e9");
  const [hasSecondary, setHasSecondary] = useState(p.existing?.hasSecondary ?? false);
  const [radio, setRadio] = useState(p.existing?.radioChannel ?? "");
  const [phone, setPhone] = useState(p.existing?.contactPhone ?? "");
  const [error, setError] = useState<string | null>(null);

  const saveMut = useMutation({
    mutationFn: async () => {
      if (!name.trim()) throw new Error("Name is required.");
      if (p.existing) {
        await api.updateOnCallRoster(p.existing.id, {
          name, color, hasSecondary,
          radioChannel: radio.trim() || null,
          contactPhone: phone.trim() || null,
        });
      } else {
        await api.createOnCallRoster({
          name, color, hasSecondary,
          radioChannel: radio.trim() || null,
          contactPhone: phone.trim() || null,
        });
      }
    },
    onSuccess: () => p.onChanged(),
    onError: (e: { message?: string }) => setError(e?.message ?? "Failed."),
  });

  const archiveMut = useMutation({
    mutationFn: () => api.archiveOnCallRoster(p.existing!.id, true),
    onSuccess: () => p.onChanged(),
  });

  return (
    <Modal
      open
      onClose={p.onClose}
      title={p.existing ? "Edit roster" : "New roster"}
      footer={
        <div className="flex items-center justify-between gap-2 w-full">
          <div>
            {p.existing && (
              <RemoveButton
                armed={archiveArmed}
                onArm={() => setArchiveArmed(true)}
                onConfirm={() => archiveMut.mutate()}
                label="Archive"
                confirmLabel="Confirm archive"
              />
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="secondary" onClick={p.onClose}>Cancel</Button>
            <Button
              onClick={() => { setError(null); saveMut.mutate(); }}
              disabled={saveMut.isPending}
            >
              {saveMut.isPending ? "Saving…" : "Save"}
            </Button>
          </div>
        </div>
      }
    >
      <div className="space-y-3 text-sm">
        <Field label="Name">
          <Input value={name} onChange={(e) => setName(e.target.value)}
            placeholder="Detectives, SWAT, K-9, Animal Control…" />
        </Field>
        <div>
          <div className="text-xs font-medium text-slate-600 mb-1">Color</div>
          <div className="flex gap-2 items-center flex-wrap">
            {COLOR_PRESETS.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => setColor(c)}
                className={
                  "w-7 h-7 rounded-full border-2 " +
                  (c === color ? "border-slate-900" : "border-slate-200")
                }
                style={{ backgroundColor: c }}
                aria-label={`Color ${c}`}
              />
            ))}
            <input
              type="color"
              value={color}
              onChange={(e) => setColor(e.target.value)}
              className="w-7 h-7 rounded cursor-pointer"
            />
          </div>
        </div>
        <label className="flex items-start gap-2 text-sm">
          <input
            type="checkbox"
            checked={hasSecondary}
            onChange={(e) => setHasSecondary(e.target.checked)}
            className="mt-0.5 h-4 w-4"
          />
          <span className="min-w-0">
            <span className="font-medium text-slate-800">Allow secondary slot</span>
            <span className="block text-xs text-slate-500">
              Adds a "Secondary" role choice on assignments. Useful for
              SWAT-style rotations with a backup officer per date.
            </span>
          </span>
        </label>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Radio channel (optional)">
            <Input value={radio} onChange={(e) => setRadio(e.target.value)} />
          </Field>
          <Field label="Phone (optional)">
            <Input value={phone} onChange={(e) => setPhone(e.target.value)} />
          </Field>
        </div>
        {error && <div className="text-sm text-red-600">{error}</div>}
      </div>
    </Modal>
  );
}
